import { v4 as uuidv4 } from 'uuid';
import { getCookie } from '../../util_functions';
import { get_api_url as getApiUrl } from '../../utils/urls';
import { asLabel, isUuid, resolveRoute, scrub } from './route_patterns';

const FLUSH_DELAY_MS = 5000;
// record() flushes the moment the queue reaches this, so no request ever carries more than 20
// events, under the ingest route's 50 per call cap (plan Contract), above which goserver answers
// 400 and the batch is lost. Raising this past 50 needs chunking in flush().
const FLUSH_AT = 20;

export const ACTIVITY_URL = getApiUrl('/v2/cms_user/activity');

// Per tab, surviving a reload so one sitting reads as one session. Storage can be blocked
// (privacy mode); a per-load id then still groups the events of that page load.
const SESSION_ID = (() => {
  try {
    const key = 'myt_activity_session_id';
    const existing = window.sessionStorage.getItem(key);
    if (existing) return existing;
    const id = uuidv4();
    window.sessionStorage.setItem(key, id);
    return id;
  } catch (e) {
    return uuidv4();
  }
})();

let queue = [];
let flushTimer = null;
// The page the user is on, attached to every interaction so a click can be read against the
// student or teacher on screen without parsing anything else.
let page = { route: '/', module: 'root' };

const send = (events, token, keepalive, attempt = 0) =>
  fetch(ACTIVITY_URL, {
    method: 'POST',
    keepalive,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ session_id: SESSION_ID, events })
  }).catch(() => {
    // fetch rejects only when the request never got an answer, so a 4xx/5xx resolves above and
    // is dropped, never retried: a rejected batch would be rejected again. One delayed retry for
    // a network blip, then the batch is dropped. Losing it costs a gap in the activity log, not
    // CMS data; a sustained loss shows up in the HRIDAYA_ACTIVITY zero-rows check.
    if (attempt === 0) {
      setTimeout(() => send(events, token, keepalive, 1), FLUSH_DELAY_MS);
    }
  });

// fetch, not axios: the pagehide flush needs `keepalive` so it survives the tab closing, which
// axios 0.19 (XHR) cannot do.
export const flush = ({ keepalive = false } = {}) => {
  clearTimeout(flushTimer);
  flushTimer = null;
  const token = getCookie('cms_token_3');
  const events = queue;
  queue = [];
  if (token && events.length) send(events, token, keepalive);
};

// The cookie is checked per event, not once at mount: the tracker mounts on the login page and
// login does not reload the app, and after logout nothing is sent under the old identity.
const record = (event) => {
  if (!getCookie('cms_token_3')) return;
  queue.push({ client_event_id: uuidv4(), occurred_at: Date.now(), ...event });
  if (queue.length >= FLUSH_AT) {
    flush();
  } else if (!flushTimer) {
    // Armed by the first event, not on an interval, so a tab nobody touches runs no timer at all.
    flushTimer = setTimeout(flush, FLUSH_DELAY_MS);
  }
};

// The last page_view this tab recorded, as the visited path plus entity. A page that rewrites only
// its query on load (Teacher Payments syncs its month with history.replace,
// teacher_payments/index.jsx) or a search that only changes the query is the same view, not a
// second one. Keyed on the path, not the route pattern: /teacher-payments/overview and
// /teacher-payments/penalties, or two /blog/:slug posts, share a pattern but are different pages.
// The raw pathname, not normalizePath: the key never leaves this tab's memory, and normalizing
// would fold /replacementsessions/123 and /replacementsessions/456 into one `:id` key and drop
// the second view. The entity tells two students on /studentprofile?id= apart.
let lastViewKey = null;

export const recordPageView = (pathname, search) => {
  page = resolveRoute(pathname, search);
  const key = `${pathname}|${page.entity_uuid || ''}`;
  if (key === lastViewKey) return;
  lastViewKey = key;
  record({ event_name: 'page_view', ...page });
};

const ACTIONABLE =
  'button, a, input, select, textarea, summary, [data-track], [role="button"], [role="link"],' +
  ' [role="tab"], [role="menuitem"], [role="option"], [role="checkbox"], [role="radio"],' +
  ' [role="switch"]';
// Form fields are recorded on change, once per committed edit; their click (focusing a text box,
// ticking a checkbox) would only double count the same edit.
const FIELD_TAGS = ['input', 'select', 'textarea'];

// Name: data-track, id, name. aria-label is deliberately not used: in this repo it interpolates
// data, `Dial ${k}` (twilio_agent/DialerModal.jsx) would spell out every dialled phone number
// and `Remove ${m.name}` (TeamsSettings.jsx, support_inbox) a person's name. ids are scrubbed
// because some embed row ids (`resolve-move-${c.audit_id}`). Text, value and innerHTML are
// never read.
export const describeControl = (el) => {
  const kind = asLabel(el.getAttribute('role') || el.tagName.toLowerCase());
  const name = asLabel(scrub(el.dataset.track || el.id || el.getAttribute('name') || ''));
  const holder = el.closest('[data-entity-uuid]');
  const heldUuid = holder && holder.dataset.entityUuid;
  const entity = isUuid(heldUuid)
    ? { entity_type: asLabel(holder.dataset.entityType || '') || undefined, entity_uuid: heldUuid }
    : { entity_type: page.entity_type, entity_uuid: page.entity_uuid };
  return {
    route: page.route,
    module: page.module,
    control: name || `unnamed:${kind}`,
    control_kind: kind,
    ...entity
  };
};

// A click target can be the document itself when code dispatches one there; it has no closest().
const onClick = (e) => {
  const el = e.target instanceof Element ? e.target.closest(ACTIONABLE) : null;
  if (!el || FIELD_TAGS.includes(el.tagName.toLowerCase())) return;
  record({ event_name: 'interaction', ...describeControl(el) });
};

const onChange = (e) => {
  if (!FIELD_TAGS.includes(e.target.tagName.toLowerCase())) return;
  record({ event_name: 'interaction', ...describeControl(e.target) });
};

const onSubmit = (e) => record({ event_name: 'form_submit', ...describeControl(e.target) });

const onHide = () => flush({ keepalive: true });
const onVisibility = () => {
  if (document.visibilityState === 'hidden') onHide();
};

// Installs everything once and returns the teardown, so a remount (React StrictMode in dev, or a
// test) never stacks a second set of listeners.
export const startTracking = () => {
  document.addEventListener('click', onClick, true);
  document.addEventListener('change', onChange, true);
  document.addEventListener('submit', onSubmit, true);
  window.addEventListener('pagehide', onHide);
  document.addEventListener('visibilitychange', onVisibility);
  return () => {
    document.removeEventListener('click', onClick, true);
    document.removeEventListener('change', onChange, true);
    document.removeEventListener('submit', onSubmit, true);
    window.removeEventListener('pagehide', onHide);
    document.removeEventListener('visibilitychange', onVisibility);
    flush();
    // A torn-down tracker has no current view; the next mount starts its own.
    lastViewKey = null;
  };
};
